import { useEffect, useState } from 'react'
import { matchPath, useHistory } from 'react-router-dom'
import { useLocation } from 'react-use'
import { ROUTES, routeByParam } from 'routes'
import { getSteps, stepNameIdx } from '../config'
import { t } from '@lingui/core/macro'

type StepSlug = keyof typeof stepNameIdx
type StepIndex = (typeof stepNameIdx)[StepSlug]

export function useTemplateNavigation({ templateId }: { templateId: string }) {
  const location = useLocation()
  const history = useHistory()
  const [activeStep, setActiveStep] = useState<StepIndex | undefined>(() => {
    const currentRoute = location.pathname?.split('/').pop()
    return currentRoute ? stepNameIdx[currentRoute as StepSlug] : undefined
  })
  const steps = getSteps()
  const isLastStep = activeStep === steps.length - 1

  const templateNavigationConfig = [
    {
      label: t`Setup`,
      path: routeByParam({ url: ROUTES.ADMIN_TEMPLATE_EDIT_SETUP, params: { id: templateId } }),
      selected: matchPath(location.pathname ?? '', ROUTES.ADMIN_TEMPLATE_EDIT_SETUP),
    },
    {
      label: t`Define and Govern`,
      path: routeByParam({ url: ROUTES.ADMIN_TEMPLATE_EDIT_GOVERN, params: { id: templateId } }),
      selected: matchPath(location.pathname ?? '', ROUTES.ADMIN_TEMPLATE_EDIT_GOVERN),
    },
    {
      label: t`Connect`,
      path: routeByParam({ url: ROUTES.ADMIN_TEMPLATE_EDIT_CONNECT, params: { id: templateId } }),
      selected: matchPath(location.pathname ?? '', ROUTES.ADMIN_TEMPLATE_EDIT_CONNECT),
    },
  ]

  const updateUrl = (slug: string) => {
    const currentUrlStepRemoved = history.location.pathname.split('/').slice(0, -1).join('/')
    const updatedUrl = `${currentUrlStepRemoved}/${slug}`

    history.replace(updatedUrl)
  }

  const handleNext = () => {
    const nextStepSlug = typeof activeStep !== 'undefined' ? steps[activeStep].next : null
    if (!nextStepSlug) {
      return
    }
    const nextStepIdx = stepNameIdx[nextStepSlug as StepSlug]
    const nextStep = steps[nextStepIdx]
    setActiveStep(nextStepIdx)
    updateUrl(nextStep.slug)
  }

  const handlePrevious = () => {
    const prevStepSlug = activeStep ? steps[activeStep].previous : null
    if (!prevStepSlug) {
      return
    }
    const prevStepIdx = stepNameIdx[prevStepSlug as StepSlug]
    const prevStep = steps[prevStepIdx]
    setActiveStep(prevStepIdx)
    updateUrl(prevStep.slug)
  }

  const handleStep = (stepIdx: StepIndex) => () => {
    setActiveStep(stepIdx)
    updateUrl(steps[stepIdx].slug)
  }

  useEffect(() => {
    const currentRoute = location.pathname?.split('/').pop()
    if (!currentRoute) {
      return
    }
    const newStepIndex = stepNameIdx[currentRoute as StepSlug] // bug if newStepIndex is undefined
    if (typeof newStepIndex !== 'undefined' && newStepIndex !== activeStep) {
      setActiveStep(newStepIndex)
    }
  }, [location.pathname])

  return {
    activeStep,
    isLastStep,
    templateNavigationConfig,
    handleNext,
    handlePrevious,
    handleStep,
  }
}
